import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// ── Ein Fenster aus dem Tab ziehen ───────────────────────────────────────────────────────────────
//
// `window.open` plus ein React-Portal in das Dokument des neuen Fensters. Entscheidend ist, was das
// NICHT ist: kein zweiter React-Baum, keine eigene Route, kein Zustandsabgleich. Es bleibt dieselbe
// JS-Umgebung — der Zustand-Store, der SSE-Kanal, der laufende Anruf-Timer und die Skript-Position
// laufen unverändert weiter, nur das DOM hängt woanders. Ein ausgelagertes Cockpit ist derselbe
// Anruf, nicht eine Kopie davon.
//
// Warum `window.open` und nicht Document-Picture-in-Picture: letzteres ist dafür gebaut, liegt immer
// oben und kennt keinen Popup-Blocker — aber nur in Chromium, und nur EIN Fenster gleichzeitig.
// Hier sollen drei Fenster nebeneinander stehen können, in jedem Browser.
//
// Was im ausgelagerten Zustand ENTFÄLLT: Ziehen, Größe ändern, Vollbild, der Fenster-Stapel. Das
// macht ab hier das Betriebssystem, und zwar besser. Die Pfade werden nicht nachgebaut, sie werden
// abgeschaltet.

// Wie lange auf die kopierten Stylesheets gewartet wird, bevor der Inhalt eingehängt wird. Ohne das
// Warten sieht man für einen Lidschlag die nackte HTML-Struktur — schlimmer als ein leeres Fenster,
// weil es nach einem kaputten Programm aussieht. Die Schranke ist da, damit ein hängendes
// Stylesheet das Fenster nicht dauerhaft leer lässt: lieber kurz unschön als nie.
const STIL_WARTE_MS = 1500;

/** Kopiert Stile und Thema in das neue Dokument.
 *
 * Drei Dinge, und alle drei sind nötig:
 *  1. `<link rel=stylesheet>` und `<style>` aus dem Kopf — im Bau ist Tailwind ein `<link>`, in der
 *     Entwicklung sind es von Vite eingefügte `<style>`-Knoten. Beide Fälle vorkommen zu lassen ist
 *     billiger, als sich auf einen zu verlassen.
 *  2. Die Attribute von `<html>` (`class`, `data-theme`) — daran hängen Hell/Dunkel.
 *  3. Der INLINE-Stil von `<html>`. Leicht zu übersehen und trotzdem der wichtigste: `applyTheme`
 *     (store/brandingSlice) schreibt die Kundenfarben als CSS-Variablen dorthin, und AppShell die
 *     Hintergrundfarbe. Ohne diese Zeile stünde das ausgelagerte Fenster in den Standardfarben da.
 */
function uebernimmStile(ziel: Window): Promise<void> {
  const doc = ziel.document;
  const quelle = document.documentElement;
  for (const attr of ['class', 'data-theme', 'lang', 'style']) {
    const wert = quelle.getAttribute(attr);
    if (wert) doc.documentElement.setAttribute(attr, wert);
  }
  doc.body.className = document.body.className;
  doc.body.style.cssText = document.body.style.cssText;
  doc.body.style.margin = '0';

  const warten: Promise<void>[] = [];
  document.querySelectorAll<HTMLElement>('link[rel="stylesheet"], style').forEach((knoten) => {
    const kopie = knoten.cloneNode(true) as HTMLElement;
    if (kopie instanceof HTMLLinkElement) {
      // Relativ angegebene Pfade lösen im `about:blank`-Dokument gegen die falsche Grundlage auf —
      // deshalb die bereits aufgelöste absolute Adresse übernehmen.
      kopie.href = (knoten as HTMLLinkElement).href;
      warten.push(new Promise<void>((fertig) => {
        kopie.addEventListener('load', () => fertig(), { once: true });
        kopie.addEventListener('error', () => fertig(), { once: true });
      }));
    }
    doc.head.appendChild(kopie);
  });

  // Von `adoptedStyleSheets` gehaltene Regeln kommen über keinen der beiden Wege mit. Heute nutzt
  // der Bau sie nicht; die Zeile kostet nichts und rettet den Tag, an dem sich das ändert.
  try {
    if (document.adoptedStyleSheets?.length) {
      doc.adoptedStyleSheets = [...document.adoptedStyleSheets];
    }
  } catch { /* Browser ohne diese Fähigkeit: die Kopien oben genügen */ }

  return Promise.race([
    Promise.all(warten).then(() => undefined),
    new Promise<void>((fertig) => ziel.setTimeout(fertig, STIL_WARTE_MS)),
  ]);
}

export interface AuslagerStand {
  fenster: Window | null;   // null, solange nichts offen ist oder die Stile noch nicht stehen
  blockiert: boolean;       // der Browser hat das Fenster verweigert (Popup-Blocker)
}

/** Öffnet ein echtes Betriebssystem-Fenster, solange `offen` gilt, und räumt es wieder weg.
 *
 * MUSS aus einem Klick heraus ausgelöst werden, sonst greift der Popup-Blocker — deshalb meldet der
 * Rückgabewert `blockiert`, statt still nichts zu tun. Ein Knopf, der nichts bewirkt und nichts
 * sagt, ist der schlechteste Ausgang von allen.
 */
export function useAusgelagertesFenster({ offen, titel, breite, hoehe, name, onZu }: {
  offen: boolean;
  titel: string;
  breite: number;
  hoehe: number;
  name: string;      // eigener Fenstername je Art — sonst holt der Browser dasselbe Fenster zurück
  onZu: () => void;  // der Benutzer hat das ausgelagerte Fenster geschlossen → zurück in den Tab
}): AuslagerStand {
  const [fenster, setFenster] = useState<Window | null>(null);
  const [blockiert, setBlockiert] = useState(false);
  // Der Rückruf darf den Effekt nicht neu anstossen — sonst schlösse und öffnete sich das Fenster
  // bei jedem Rendern des Aufrufers.
  const onZuRef = useRef(onZu);
  onZuRef.current = onZu;

  useEffect(() => {
    if (!offen) { setBlockiert(false); return; }

    const w = window.open('', name, `popup=yes,width=${breite},height=${hoehe}`);
    if (!w) { setBlockiert(true); return; }
    setBlockiert(false);
    w.document.title = titel;

    // Beim absichtlichen Aufräumen (Rückkehr in den Tab, Ausbau der Komponente, doppelter Effekt im
    // Strict-Modus) darf `onZu` NICHT feuern — sonst meldete das Fenster seine eigene Schliessung
    // als Benutzerwunsch zurück und der Zustand kippte hin und her.
    let raeumtSelbst = false;
    const beimSchliessen = () => { if (!raeumtSelbst) onZuRef.current(); };
    w.addEventListener('pagehide', beimSchliessen);
    // Der Tab geht zu: das Kindfenster mitnehmen. Ein zurückbleibendes Fenster mit totem React
    // sähe aus wie eine eingefrorene Anwendung.
    const elternZu = () => { raeumtSelbst = true; w.close(); };
    window.addEventListener('pagehide', elternZu);

    let abgebaut = false;
    void uebernimmStile(w).then(() => { if (!abgebaut && !w.closed) setFenster(w); });

    return () => {
      abgebaut = true;
      raeumtSelbst = true;
      w.removeEventListener('pagehide', beimSchliessen);
      window.removeEventListener('pagehide', elternZu);
      setFenster(null);
      if (!w.closed) w.close();
    };
  }, [offen, name, titel, breite, hoehe]);

  return { fenster, blockiert };
}

/** Hängt den Inhalt in das ausgelagerte Fenster. Ausserhalb des Portals bleibt der Aufrufer im
 * Haupt-Baum — seine Hooks (Tastatur, Timer) laufen dort weiter und müssen deshalb selbst wissen,
 * an welchem Fenster sie horchen. Genau dafür gibt `useAusgelagertesFenster` das Fenster zurück,
 * statt es zu verstecken. */
export function AusgelagerterInhalt({ fenster, children }: {
  fenster: Window | null;
  children: React.ReactNode;
}) {
  if (!fenster) return null;
  return createPortal(children, fenster.document.body);
}
